<template>
  <Dialog :visible="addUserDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Add User
    </template>

    <div class="form-element">
      Email:
      <InputText v-model="newUserEmail" :invalid="newUserEmailInvalid" class="form-element full-width" type="text"
                 @change="newUserEmailChange"/>
      <div v-if="newUserEmailExists">
        <InlineMessage severity="error">A user account with this email address already exists.</InlineMessage>
      </div>
      <div v-else-if="newUserEmailInvalid">
        <InlineMessage severity="error">Email is required.</InlineMessage>
      </div>
    </div>
    <div class="form-element">
      First Name:
      <InputText v-model="newUserFirstName" :invalid="newUserFirstNameInvalid" class="form-element full-width" type="text"
                 @change="newUserFirstNameChange"/>
      <div v-if="newUserFirstNameInvalid">
        <InlineMessage severity="error">First Name is required.</InlineMessage>
      </div>
    </div>
    <div class="form-element">
      Last Name:
      <InputText v-model="newUserLastName" :invalid="newUserLastNameInvalid" class="form-element full-width" type="text"
                 @change="newUserLastNameChange"/>
      <div v-if="newUserLastNameInvalid">
        <InlineMessage severity="error">Last Name is required.</InlineMessage>
      </div>
    </div>
    <div class="form-element">
      Roles:
      <MultiSelect v-model="newUserRoles"
                   display="chip"
                   :showToggleAll="false"
                   :options="roleOptions"
                   optionLabel="name"
                   optionValue="id"
                   placeholder="User Roles"
                   class="full-width"
      ></MultiSelect>
    </div>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="newUserSubmitting" :loading="newUserSubmitting" label="Add" type="button" @click="addUser"></Button>
      </div>
    </template>
  </Dialog>
  <Dialog :visible="manageUserQuotasDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage {{ userToManage?.name }} Quotas
    </template>


    <div class="full-width flex">
      <Dropdown :options="availableQuotas"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedQuota"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addQuotaToUserSubmitting" @click="addQuotaToUser(selectedQuota)" :disabled="!selectedQuota" label="Add Quota"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <DataTable :value="userToManageQuotaAssignments">
        <Column field="name" header="Name"></Column>
        <Column field="dailyQuota" header="Daily Limit"></Column>
        <Column field="monthlyQuota" header="Monthly Limit"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteUserQuotaAssignment(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeQuotaFromUserSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>
  <Dialog :visible="manageUserDeploymentsDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage {{ userToManage?.name }} LLM Deployments
    </template>


    <div class="full-width flex">
      <Dropdown :options="availableDeployments"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedDeployment"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addDeploymentToUserSubmitting" @click="addDeploymentToUser(selectedDeployment)" :disabled="!selectedDeployment" label="Add Deployment"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <DataTable :value="userToManageDeploymentAccess">
        <Column field="name" header="Name"></Column>
        <Column field="modelName" header="Model"></Column>
        <Column field="status" header="Status"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteUserDeploymentAccess(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeDeploymentFromUserSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>
  <Dialog :visible="manageUserGroupsDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage User Groups
    </template>
    <div class="full-width flex">
      <Dropdown :options="availableGroups"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedGroup"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addUserToGroupSubmitting" @click="addUserToGroup(selectedGroup)" :disabled="!selectedGroup" label="Add Group"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 35vh">
      <DataTable :value="userToManageGroupMemberships">
        <Column field="name" header="Name"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteUserGroupMembership(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeUserFromGroupSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>
  <Dialog :visible="manageUserDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage User
    </template>

    <div class="form-element">
      Roles:
      <MultiSelect v-model="userToManage.roles"
                   display="chip"
                   :showToggleAll="false"
                   :options="roleOptions"
                   optionLabel="name"
                   optionValue="id"
                   placeholder="User Roles"
                   class="full-width"
      ></MultiSelect>
    </div>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageUserSubmitting" :loading="manageUserSubmitting" label="Save" type="button" @click="saveUser"></Button>
      </div>
    </template>
  </Dialog>

  <div v-if="canManageUsers">
    <ProgressSpinnerContainer v-if="usersLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2>Users</h2>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="computedUsers">
          <Column field="name" header="Name"></Column>
          <Column field="email" header="Email"></Column>
          <Column field="roles" header="Roles">
            <template #body="slotProps">
              <div v-for="role in slotProps.data.roles" :key="role.id">
                {{ role.name }}
              </div>
            </template>
          </Column>
          <Column field="groups" header="Groups">
            <template #body="slotProps">
              <div v-for="group in slotProps.data.groups" :key="group.id">
                {{ group.name }}
              </div>
            </template>
          </Column>
          <Column field="quotas" header="Quotas">
            <template #body="slotProps">
              <div v-for="quota in slotProps.data.quotas" :key="quota.id">
                {{ quota.name }}
              </div>
            </template>
          </Column>
          <Column field="deployments" header="LLM Deployments">
            <template #body="slotProps">
              <div v-for="deployment in slotProps.data.deployments" :key="deployment.id">
                {{ deployment.name }}
              </div>
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <template v-if="1 || slotProps.data.auth0Id !== user.sub">
                <Button severity="info" @click="manageUser(slotProps.data)" label="Edit"></Button>
                <Button class="ml" severity="info" @click="manageUserGroups(slotProps.data)" label="Manage Groups"></Button>
                <Button class="ml" severity="info" @click="manageUserQuotas(slotProps.data)" label="Manage Quotas"></Button>
                <Button class="ml" severity="info" @click="manageUserDeployments(slotProps.data)" label="Manage LLM Deployments"></Button>
                <Button v-if="slotProps.data.blocked" :disabled="slotProps.data.blockedLoading" :loading="slotProps.data.blockedLoading" class="ml" severity="primary" @click="unblockUser(slotProps.data)" label="Unblock"></Button>
                <Button v-else :disabled="slotProps.data.blockedLoading" :loading="slotProps.data.blockedLoading" class="ml" severity="danger" @click="blockUser(slotProps.data)" label="Block"></Button>
              </template>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useAuth0 } from "@auth0/auth0-vue";
import { useConfirm } from "primevue/useconfirm";
import { useRouter } from "vue-router";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import { useToast } from "primevue/usetoast";
import { computed, ref, unref, watch } from "vue";
import { loadUsers, useAccounts, useLlmQuotas, useUsers, useLlmModelDeploymentAccess, useModelDeployments, loadLlmQuotas, loadLlmModelDeploymentAccess, loadModelDeployments } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import {
  addUserRoles,
  doBlockUser,
  doUnblockUser,
  postUser,
  inviteUser,
  removeUserRoleAssignments,
  deleteRequest, post
} from "../util/api.js";
import { decodeLlmQuotaAssignment, decodeUserGroupMembership, decodeLlmModelDeploymentAccess } from "../util/util.js";

const { user } = useAuth0()

const { selectedAccountDetails } = useAccounts()

const { users, userGroups, userGroupMemberships, roles, usersLoading, canManageUsers } = useUsers()

const { llmModelDeploymentAccess } = useLlmModelDeploymentAccess()
const { modelDeployments } = useModelDeployments()

const computedUsers = computed(() => {
  const output = []
  for (let i = 0; i < users.value.length; i++) {
    const o = { ...users.value[i] }
    const groups = {}
    const groupMemberships = userGroupMemberships.value.filter(gm => String(gm.userId) === String(o.id))
    for (let j = 0; j < groupMemberships.length; j++) {
      const userGroup = userGroups.value.find(g => String(g.id) === String(groupMemberships[j].groupId))
      groups[userGroup.id] = {
        id: userGroup.id,
        name: userGroup.name
      }
    }
    o.groups = Object.values(groups).sort((a, b) => a.name < b.name)

    const quotas = {}
    const quotasAssignments = llmQuotaAssignments.value.filter(qa => String(qa.userId) === String(o.id))
    for (let j = 0; j < quotasAssignments.length; j++) {
      const llmQuota = llmQuotas.value.find(q => String(q.id) === String(quotasAssignments[j].llmQuotaId))
      quotas[llmQuota.id] = {
        id: llmQuota.id,
        name: llmQuota.name
      }
    }
    o.quotas = Object.values(quotas).sort((a, b) => a.name < b.name)

    const deployments = {}
    const deploymentAccess = llmModelDeploymentAccess.value.filter(da => String(da.userId) === String(o.id))
    for (let j = 0; j < deploymentAccess.length; j++) {
      const modelDeployment = modelDeployments.value.find(d => String(d.id) === String(deploymentAccess[j].llmModelDeploymentId))
      if (modelDeployment) {
        deployments[modelDeployment.id] = {
          id: modelDeployment.id,
          name: modelDeployment.name
        }
      }
    }
    o.deployments = Object.values(deployments).sort((a, b) => a.name < b.name)

    output.push(o)
  }
  return output
})

watch(usersLoading, (newValue)=> {
  if (!newValue) {
    if (!canManageUsers.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

// Load additional data when component loads
watch(selectedAccount, async (newValue) => {
  if (newValue) {
    await Promise.all([
      loadLlmQuotas(),
      loadLlmModelDeploymentAccess(),
      loadModelDeployments()
    ])
  }
}, { immediate: true })

const roleOptions = computed(() => {
  return Object.keys(roles.value).map((key) => {
      return { id: roles.value[key].id, name: roles.value[key].name }
    }
  );
})

const newUserEmail = ref('')
const newUserFirstName = ref('')
const newUserLastName = ref('')
const newUserEmailInvalid = ref(false)
const newUserEmailExists = ref(false)
const newUserFirstNameInvalid = ref(false)
const newUserLastNameInvalid = ref(false)
const newUserSubmitting = ref(false)
const newUserRoles = ref([])
const addUserDialogVisible = ref(false)
const manageUserDialogVisible = ref(false)
const manageUserSubmitting = ref(false)
const userToManage = ref(null)

function newUserEmailChange(v) {
  newUserEmailInvalid.value = v.length > 0
  newUserEmailExists.value = false
}
function newUserFirstNameChange(v) {
  newUserFirstNameInvalid.value = v.length > 0
}
function newUserLastNameChange(v) {
  newUserLastNameInvalid.value = v.length > 0
}

const toast = useToast()

const confirm = useConfirm()

function resetValidations() {
  newUserEmailInvalid.value = false
  newUserEmailExists.value = false
  newUserFirstNameInvalid.value = false
  newUserLastNameInvalid.value = false
}

async function addUser() {
  let valid = true
  resetValidations()
  if (!newUserEmail.value) {
    newUserEmailInvalid.value = true
    valid = false
  }
  for (let i = 0; i < users.value?.length; i++) {
    if (users.value[i]?.email === newUserEmail.value) {
      newUserEmailExists.value = true
      valid = false
    }
  }
  if (!newUserFirstName.value) {
    newUserFirstNameInvalid.value = true
    valid = false
  }
  if (!newUserLastName.value) {
    newUserLastNameInvalid.value = true
    valid = false
  }
  if (!valid) {
    return
  }
  newUserSubmitting.value = true
  let success = false
  /* Store these here so that we can reset them below and then use them later to send the invitation */
  const account = selectedAccount.value
  const email = newUserEmail.value
  const firstName = newUserFirstName.value
  const lastName = newUserLastName.value
  const roles = newUserRoles.value
  try {
    await postUser(account, email, firstName, lastName, roles)
    success = true
  }
  catch (e) {
    toast.add({
      severity: 'error',
      group: 'mainToast',
      life: 5000,
      detail: 'An error has occurred.  Please try again.'
    })
  }
  handleClose()
  if (success) {
    loadUsers()
    try {
      const wl = selectedAccountDetails.value?.whitelabel
      await inviteUser(account, email, firstName, lastName, wl)
    }
    catch (e) {
      toast.add({
        severity: 'error',
        group: 'mainToast',
        life: 5000,
        detail: 'Your invitation was created, but the notification could not be sent.'
      })
    }
  }
}

function showAddUserDialog() {
  addUserDialogVisible.value = true
}

function handleClose() {
  newUserEmail.value = ''
  newUserFirstName.value = ''
  newUserLastName.value = ''
  newUserRoles.value = []
  newUserEmailInvalid.value = false
  newUserEmailExists.value = false
  newUserFirstNameInvalid.value = false
  newUserLastNameInvalid.value = false
  userToManage.value = null
  newUserSubmitting.value = false
  manageUserSubmitting.value = false
  addUserDialogVisible.value = false
  manageUserDialogVisible.value = false
  addUserToGroupSubmitting.value = false
  removeUserFromGroupSubmitting.value = false
  manageUserGroupsDialogVisible.value = false
  addQuotaToUserSubmitting.value = false
  removeQuotaFromUserSubmitting.value = false
  manageUserQuotasDialogVisible.value = false
  addDeploymentToUserSubmitting.value = false
  removeDeploymentFromUserSubmitting.value = false
  manageUserDeploymentsDialogVisible.value = false
}

async function saveUser() {
  manageUserSubmitting.value = true
  const roles = userToManage.value.roles
  const roleAssignments = userToManage.value.roleAssignments

  const rolesToAdd = []
  const rolesToRemove = []

  for (let i = 0; i < roles.length; i++) {
    let found = false
    for (let j = 0; j < roleAssignments.length; j++) {
      if (roleAssignments[j].roleId === roles[i]) {
        found = true
        break
      }
    }
    if (!found) {
      rolesToAdd.push(roles[i])
    }
  }

  for (let i = 0; i < roleAssignments.length; i++) {
    let found = false
    for (let j = 0; j < roles.length; j++) {
      if (roleAssignments[i].roleId === roles[j]) {
        found = true
        break
      }
    }
    if (!found) {
      rolesToRemove.push(roleAssignments[i])
    }
  }

  await addUserRoles(selectedAccount, userToManage.value.id, rolesToAdd)
  await removeUserRoleAssignments(selectedAccount, userToManage.value.id, rolesToRemove)
  handleClose()
  await loadUsers()
}

function manageUser(user) {
  userToManage.value = {
    id: user.id,
    roles: [],
    roleAssignments: user.roles
  }
  for (let i = 0; i < user.roles.length; i++) {
    userToManage.value.roles.push(user.roles[i].roleId)
  }
  manageUserDialogVisible.value = true
}

function manageUserGroups(user) {
  userToManage.value = {
    id: user.id,
    name: user.name
  }
  manageUserGroupsDialogVisible.value = true
}

function manageUserQuotas(user) {
  userToManage.value = {
    id: user.id,
    name: user.name
  }
  manageUserQuotasDialogVisible.value = true
}

function manageUserDeployments(user) {
  userToManage.value = {
    id: user.id,
    name: user.name
  }
  manageUserDeploymentsDialogVisible.value = true
}


async function unblockUser(user) {
  unref(user).blockedLoading = true
  try {
    await doUnblockUser(selectedAccount, unref(user)?.id)
    unref(user).blocked = false
  }
  catch (e) {
    /* @todo handle error */
  }
  unref(user).blockedLoading = false
}
async function blockUser(user) {
  unref(user).blockedLoading = true
  try {
    await doBlockUser(selectedAccount, unref(user)?.id)
    unref(user).blocked = true
  }
  catch (e) {
    /* @todo handle error */
  }
  unref(user).blockedLoading = false
}

function handleError(e) {
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}


function deleteUserGroupMembership(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + userToManage.value.name + ' from ' + data.name + '?',
    header: 'Confirm User Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeUserFromGroupSubmitting.value = true
        for (let i = 0; i < userGroupMemberships.value?.length; i++) {
          if (
              (String(userGroupMemberships.value[i].groupId) === String(data.id)) &&
              (String(userGroupMemberships.value[i].userId) === String(userToManage.value.id))
          ) {
            const request = await deleteRequest('/v1/cephisso-platform-user-group-memberships/' + userGroupMemberships.value[i].id)
            if (request.id) {
              userGroupMemberships.value = userGroupMemberships.value.filter(gm => String(gm.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeUserFromGroupSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

const selectedGroup = ref(null)
const addUserToGroupSubmitting = ref(false)
const removeUserFromGroupSubmitting = ref(false)
const manageUserGroupsDialogVisible = ref(false)

const userToManageGroupMemberships = computed(() => {
  const output = []
  if (!userToManage.value) {
    return output
  }
  for (let i = 0; i < userGroupMemberships.value.length; i++) {
    if (String(userGroupMemberships.value[i].userId) === String(userToManage.value.id)) {
      const group = userGroups.value.find(g => String(g.id) === String(userGroupMemberships.value[i].groupId))
      if (group) {
        output.push({
          groupMembershipId: userGroupMemberships.value[i].id,
          ...group
        })
      }
    }
  }
  return output
})

const availableGroups = computed(() => userGroups.value.filter(g => !userToManageGroupMemberships.value?.find(a => String(a.groupId) === String(g.id))))

async function addUserToGroup(id) {
  const payload = {
    cephisso_platform_users_id: userToManage.value.id,
    cephisso_platform_user_groups_id: id,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addUserToGroupSubmitting.value = true
  const request = await post('/v1/cephisso-platform-user-group-memberships', payload)
  if (request.id) {
    userGroupMemberships.value.push(decodeUserGroupMembership(request))
  }
  else {
    handleError()
  }
  addUserToGroupSubmitting.value = false
  selectedGroup.value = null
}

const selectedQuota = ref(null)
const addQuotaToUserSubmitting = ref(false)
const removeQuotaFromUserSubmitting = ref(false)
const manageUserQuotasDialogVisible = ref(false)

const selectedDeployment = ref(null)
const addDeploymentToUserSubmitting = ref(false)
const removeDeploymentFromUserSubmitting = ref(false)
const manageUserDeploymentsDialogVisible = ref(false)

const { llmQuotas, llmQuotaAssignments } = useLlmQuotas()

const userToManageQuotaAssignments = computed(() => {
  const output = []
  if (!userToManage.value) {
    return output
  }
  for (let i = 0; i < llmQuotaAssignments.value.length; i++) {
    if (String(llmQuotaAssignments.value[i].userId) === String(userToManage.value.id)) {
      const q = llmQuotas.value.find(q => String(q.id) === String(llmQuotaAssignments.value[i].llmQuotaId))
      if (q) {
        output.push({
          quotaAssignmentId: llmQuotaAssignments.value[i].id,
          ...q
        })
      }
    }
  }
  return output
})

const availableQuotas = computed(() => llmQuotas.value.filter(g => !userToManageQuotaAssignments.value?.find(a => String(a.id) === String(g.id))))

const userToManageDeploymentAccess = computed(() => {
  const output = []
  if (!userToManage.value) {
    return output
  }
  for (let i = 0; i < llmModelDeploymentAccess.value.length; i++) {
    if (String(llmModelDeploymentAccess.value[i].userId) === String(userToManage.value.id)) {
      const d = modelDeployments.value.find(d => String(d.id) === String(llmModelDeploymentAccess.value[i].llmModelDeploymentId))
      if (d) {
        output.push({
          deploymentAccessId: llmModelDeploymentAccess.value[i].id,
          ...d
        })
      }
    }
  }
  return output
})

const availableDeployments = computed(() => modelDeployments.value.filter(d => 
  !userToManageDeploymentAccess.value?.find(a => String(a.id) === String(d.id)) &&
  d.platformAccountId === selectedAccount.value &&
  d.status === 'ACTIVE'
))

async function addQuotaToUser(id) {
  const payload = {
    ai_credit_quotas_id: id,
    cephisso_platform_users_id: userToManage.value.id,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addQuotaToUserSubmitting.value = true
  const request = await post('/crbn-tech/ai-credit-quota-assignments', payload)
  if (request.id) {
    llmQuotaAssignments.value.push(decodeLlmQuotaAssignment(request))
  }
  else {
    handleError()
  }
  addQuotaToUserSubmitting.value = false
  selectedQuota.value = null
}

async function addDeploymentToUser(id) {
  const payload = {
    llm_model_deployments_id: id,
    cephisso_platform_users_id: userToManage.value.id,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addDeploymentToUserSubmitting.value = true
  const request = await post('/crbn-tech/llm-model-deployment-access', payload)
  if (request.id) {
    llmModelDeploymentAccess.value.push(decodeLlmModelDeploymentAccess(request))
  }
  else {
    handleError()
  }
  addDeploymentToUserSubmitting.value = false
  selectedDeployment.value = null
}

function deleteUserQuotaAssignment(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + data.name + ' from ' + userToManage.value.name + '?',
    header: 'Confirm User Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeQuotaFromUserSubmitting.value = true
        for (let i = 0; i < llmQuotaAssignments.value?.length; i++) {
          if (
              (String(llmQuotaAssignments.value[i].llmQuotaId) === String(data.id)) &&
              (String(llmQuotaAssignments.value[i].userId) === String(userToManage.value.id))
          ) {
            const request = await deleteRequest('/crbn-tech/ai-credit-quota-assignments/' + llmQuotaAssignments.value[i].id)
            if (request.id) {
              llmQuotaAssignments.value = llmQuotaAssignments.value.filter(qa => String(qa.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeQuotaFromUserSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

function deleteUserDeploymentAccess(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + data.name + ' from ' + userToManage.value.name + '?',
    header: 'Confirm User Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeDeploymentFromUserSubmitting.value = true
        for (let i = 0; i < llmModelDeploymentAccess.value?.length; i++) {
          if (
              (String(llmModelDeploymentAccess.value[i].llmModelDeploymentId) === String(data.id)) &&
              (String(llmModelDeploymentAccess.value[i].userId) === String(userToManage.value.id))
          ) {
            const request = await deleteRequest('/crbn-tech/llm-model-deployment-access/' + llmModelDeploymentAccess.value[i].id)
            if (request.id) {
              llmModelDeploymentAccess.value = llmModelDeploymentAccess.value.filter(da => String(da.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeDeploymentFromUserSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

</script>

<style scoped>
</style>